import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, Trash2 } from "lucide-react";
import ConfirmationDialog from "../../../Components/ConfirmationDialogs/ConfirmationDialogs";
import { showSuccessToast, showErrorToast } from "../../../toastmsg";
import { db } from "../../../firebase";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
const functions = getFunctions();
const deleteUserAuth = httpsCallable(functions, "deleteUserAuth");

interface User {
  id: string;
  displayName: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  password: string;
}

const UserListing = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [dropdownUserId, setDropdownUserId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchUsers = async () => {
    const querySnapshot = await getDocs(collection(db, "users"));
    const userList: User[] = querySnapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    })) as User[];
    setUsers(userList);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (location.state?.refresh) {
      fetchUsers();

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isDropdownButton = target.closest("[data-dropdown-button]");
      const isDropdownMenu = target.closest("[data-dropdown-menu]");
      if (!isDropdownButton && !isDropdownMenu) {
        setDropdownUserId(null);
        setDropdownPosition(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleEdit = (userId: string) => {
    navigate(`/user-listing/edit-user/${userId}`);
  };

  const handleAddUser = () => {
    navigate("/user-listing/add-user");
  };

  const handleDelete = async () => {
    if (!userToDelete) return;

    try {
      await deleteUserAuth({ uid: userToDelete.id });

      // 2. Delete user document from Firestore
      await deleteDoc(doc(db, "users", userToDelete.id));

      // 3. Update local state
      setUsers((prev) => prev.filter((u) => u.id !== userToDelete.id));
      setShowConfirmDialog(false);
      setUserToDelete(null);
      showSuccessToast("User deleted successfully");
    } catch (error: any) {
      console.error("Error deleting user:", error);
      showErrorToast(
        error?.message || "Failed to delete user from authentication"
      );
    }
  };

  const cancelDelete = () => {
    setShowConfirmDialog(false);
    setUserToDelete(null);
  };

  const toggleDropdown = (userId: string, event: React.MouseEvent) => {
    if (dropdownUserId === userId) {
      setDropdownUserId(null);
      setDropdownPosition(null);
    } else {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      setDropdownUserId(userId);
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
  };

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">Users</h2>
        <button
          onClick={handleAddUser}
          className="w-32 bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700"
        >
          Add User
        </button>
      </div>

      <div className="relative h-[500px] overflow-auto border rounded">
        <table className="w-full text-sm text-center">
          <thead>
            <tr className="bg-gray-300 sticky top-0 z-10">
              <th className="p-1">No</th>
              <th className="p-1">Display Name</th>
              <th className="p-1">First Name</th>
              <th className="p-1">Last Name</th>
              <th className="p-1">Email</th>
              <th className="p-1">Role</th>
              <th className="p-1">Action</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user, index) => (
              <tr key={user.id} className="border-b hover:bg-gray-100">
                <td className="p-1">{index + 1}</td>
                <td className="p-1">{user.displayName}</td>
                <td className="p-1">{user.firstName}</td>
                <td className="p-1">{user.lastName}</td>
                <td className="p-1">{user.email}</td>
                <td className="p-1">{user.role}</td>
                <td className="p-1">
                  <div className="relative">
                    <button
                      data-dropdown-button
                      onClick={(e) => toggleDropdown(user.id, e)}
                      className="text-gray-700 hover:text-black focus:outline-none"
                    >
                      <Menu className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dropdownUserId && dropdownPosition && (
        <div
          data-dropdown-menu
          className="fixed z-50 w-32 bg-white border rounded shadow"
          style={{
            top: dropdownPosition.top - 50,
            left: dropdownPosition.left - 130,
          }}
        >
          <button
            onClick={() => {
              if (dropdownUserId) handleEdit(dropdownUserId);
            }}
            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
          >
            Edit
          </button>
          {/* <button
            onClick={() => {
              const user = users.find((u) => u.id === dropdownUserId);
              if (user) confirmDeleteUser(user);
            }}
            className="block w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100"
          >
            Delete
          </button> */}
        </div>
      )}

      <ConfirmationDialog
        isOpen={showConfirmDialog}
        icon={
          <Trash2 className="w-12 h-12 p-2 bg-red-100 rounded-xl text-red-600" />
        }
        title="Delete"
        message="Are you sure you want to delete?"
        onCancel={cancelDelete}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default UserListing;
